import { makeAutoObservable } from 'mobx';
import { IUserCommunicationsProvider } from '../providers';
import { ChannelGroupsEnum, ChannelNamesEnum, IChannel } from '../interfaces';
import { IChangeCommunicationChannelRequest } from '../providers/IChangeUserCommunicationChannels.request';
import { CommunicationChannel } from './CommunicationChannel';
import ToastifyService from 'src/services/ToastifyService';
import i18n from 'src/i18n';

export class UserCommunicationsChannelsStore {
  hasError: boolean = false;
  isLoading: boolean = true;
  private submitChangeTimeout: NodeJS.Timeout | null = null;

  channels: Map<IChannel['name'], CommunicationChannel> = new Map();

  constructor(readonly service: IUserCommunicationsProvider) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.init();
  }

  async loadPreferences() {
    this.isLoading = true;
    try {
      const result = await this.service.getUserPreferences();
      for (const disabledChannel of result.disabledChannels || []) {
        const channel = this.channels.get(disabledChannel.channel);
        channel?.disable(disabledChannel.group);
      }
    } catch (error) {
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  private async changeChannels() {
    this.isLoading = true;
    try {
      const channels: IChangeCommunicationChannelRequest[] = [];
      for (const channel of Array.from(this.channels.values())) {
        for (const groupName in channel.groups) {
          if (channel.isChanged(groupName as ChannelGroupsEnum)) {
            channels.push({ channel: channel.name, enable: channel.groups[groupName].isEnabled, group: groupName });
          }
        }
      }

      if (channels.length) {
        const { errored } = await this.service.changeUserChannels({ channels });
        for (const erroredChannel of errored) {
          const channel = this.channels.get(erroredChannel.channel);
          channel?.flip(erroredChannel.group);
          ToastifyService.toast(
            `Не удалось поменять коммуникацию (${i18n.t(`channelName.${erroredChannel.channel}`)}: ${i18n.t(
              `channelGroup.${erroredChannel.group}`,
              { ns: 'channels' },
            )}) ${erroredChannel.reason}`,
            { type: 'error' },
          );
        }

        this.copyActual();
      }
    } catch (error) {
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  public onChangeCommunicationChannel(channelName: ChannelNamesEnum, channelGroup: ChannelGroupsEnum) {
    const existsChannel = this.channels.get(channelName);
    existsChannel?.flip(channelGroup);

    if (this.submitChangeTimeout) {
      clearInterval(this.submitChangeTimeout);
    }

    this.submitChangeTimeout = setTimeout(this.changeChannels.bind(this), 1500);
  }

  private init() {
    for (const name of Object.values(ChannelNamesEnum)) {
      const groups = {} as IChannel['groups'];
      Object.values(ChannelGroupsEnum).forEach((val) => {
        groups[val] = { isEnabled: true, initial: true, name: val };
      });
      this.channels.set(name, CommunicationChannel.new(name, groups));
    }
  }

  private copyActual() {
    for (const channel of Array.from(this.channels.values())) {
      channel.copyActual();
    }
  }
}
